import { Chip, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { EFFORT_STATUS_TYPE_ENUM } from 'enums';
import { alpha, useTheme } from '@mui/material/styles';

const pickColor = (status: number) => {
  switch (status) {
    case EFFORT_STATUS_TYPE_ENUM.IN_SALES:
      return 'info';
    case EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL:
      return 'warning';
    case EFFORT_STATUS_TYPE_ENUM.APPROVED:
      return 'success';
    case EFFORT_STATUS_TYPE_ENUM.REJECTED:
      return 'error';
    case EFFORT_STATUS_TYPE_ENUM.WORK_STARTED:
      return 'primary';
    case EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER:
      return 'info';
    case EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED:
      return 'secondary';
    case EFFORT_STATUS_TYPE_ENUM.INVOICED:
      return 'success';
    default:
      return 'grey';
  }
};

const getStatusConfig = (status: number, theme: any) => {
  const colorKey = pickColor(status);

  if (colorKey === 'grey') {
    const base = theme.palette.text.primary;
    return {
      label: status,
      bg: alpha(base, theme.palette.mode === 'dark' ? 0.18 : 0.08),
      color: theme.palette.text.primary,
      border: alpha(base, theme.palette.mode === 'dark' ? 0.35 : 0.25),
    };
  }

  const main = theme.palette[colorKey].main;
  return {
    label: getStatusLabel(status),
    bg: alpha(main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
    color: main,
    border: alpha(main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
  };
};

export const getStatusLabel = (status: number): JSX.Element => {
  switch (status) {
    case EFFORT_STATUS_TYPE_ENUM.IN_SALES:
      return <FormattedMessage id="enum.effortStatusType.inSales" defaultMessage="Satış Ekibinde" />;
    case EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL:
      return (
        <FormattedMessage id="enum.effortStatusType.pendingCustomerApproval" defaultMessage="Müşteri Onayı Bekliyor" />
      );
    case EFFORT_STATUS_TYPE_ENUM.APPROVED:
      return <FormattedMessage id="enum.effortStatusType.approved" defaultMessage="Onaylandı" />;
    case EFFORT_STATUS_TYPE_ENUM.REJECTED:
      return <FormattedMessage id="enum.effortStatusType.rejected" defaultMessage="Reddedildi" />;
    case EFFORT_STATUS_TYPE_ENUM.WORK_STARTED:
      return <FormattedMessage id="enum.effortStatusType.workStarted" defaultMessage="Çalışma Başladı" />;
    case EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER:
      return <FormattedMessage id="enum.effortStatusType.testingByCustomer" defaultMessage="Müşteri Testinde" />;
    case EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED:
      return <FormattedMessage id="enum.effortStatusType.workCompleted" defaultMessage="Çalışma Tamamlandı" />;
    case EFFORT_STATUS_TYPE_ENUM.INVOICED:
      return <FormattedMessage id="enum.effortStatusType.invoiced" defaultMessage="Faturalandı" />;
    default:
      return <>{status}</>;
  }
};

const getNextStatusLabel = (status: number) => {
  const map: Record<number, JSX.Element | null> = {
    [EFFORT_STATUS_TYPE_ENUM.IN_SALES]: <FormattedMessage id="enum.effortStatusType.pendingCustomerApproval" />,
    [EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL]: <FormattedMessage id="enum.effortStatusType.approved" />,
    [EFFORT_STATUS_TYPE_ENUM.APPROVED]: <FormattedMessage id="enum.effortStatusType.workStarted" />,
    [EFFORT_STATUS_TYPE_ENUM.WORK_STARTED]: <FormattedMessage id="enum.effortStatusType.testingByCustomer" />,
    [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER]: <FormattedMessage id="enum.effortStatusType.workCompleted" />,
    [EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED]: <FormattedMessage id="enum.effortStatusType.invoiced" />,
  };
  return map[status] ?? null;
};

const StatusTooltipContent = ({ status, showNextHint }: { status: number; showNextHint: boolean }) => {
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600 }}>
        <FormattedMessage id="effortTracing.currentStatus" defaultMessage="Mevcut Durum" />
      </div>
      <div>{getStatusLabel(status)}</div>
      {showNextHint && getNextStatusLabel(status) && (
        <>
          <div style={{ marginTop: 8, fontWeight: 600 }}>
            <FormattedMessage id="effortTracing.nextStep" defaultMessage="Sonraki Adım" />
          </div>
          <div>{getNextStatusLabel(status)}</div>
        </>
      )}
    </div>
  );
};

const StatusChip = ({ status, showNextHint = true }: { status: number; showNextHint?: boolean }) => {
  const theme = useTheme();
  const config = getStatusConfig(status, theme);

  return (
    <Tooltip arrow title={<StatusTooltipContent status={status} showNextHint={showNextHint} />}>
      <Chip
        size="small"
        label={config.label}
        sx={{
          fontWeight: 600,
          bgcolor: config.bg,
          color: config.color,
          border: `1px solid ${config.border}`,
          cursor: 'default',
        }}
      />
    </Tooltip>
  );
};

export default StatusChip;
